// ConfirmDialog — the decision dialog, per COMPONENTS §Catalog. A thin recipe
// over <Modal> for "are you sure?" moments and NOTHING else: a question title,
// a consequence statement, Cancel + one explicit verb. Content display (record
// previews, contact cards) is NOT this — that's SlideOver / Popover / raw
// Modal per COMPONENTS §Modal vs SlideOver.
//
// Contract (architect-locked):
// - The confirm button carries an explicit verb ("Delete user"), never OK/Yes.
// - Focus lands on Cancel on open (safe default for destructive actions);
//   when `typeToConfirm` is set, focus lands on the input instead (the user
//   must type anyway — the disabled confirm is the safety).
// - `onConfirm` may return a promise: while pending, the confirm button shows
//   its spinner and BOTH buttons + Escape/backdrop/X lock (no double-fire, no
//   cancel-mid-delete). The dialog closes on success; on failure it STAYS OPEN
//   with an inline <Alert> first in the body — the dialog words the rejection
//   ITSELF: a refused answer by its code (errors.json — an act's lost race says
//   why in the act's own words, ADR-084 §5), a worded Error by its words, else
//   an i18n'd fallback; a busy answer says nothing (the door's toast) — so the
//   user can retry — never a silent close, never a host wrapping the error.
// - `typeToConfirm` (GitHub-style escalation, for the catastrophic ops):
//   confirm stays disabled until the typed text matches exactly.
// - The discard-unsaved-changes prompt is a CONSUMER of this dialog (see the
//   DiscardChanges story); the dirty-detection wiring lands with the form-shell
//   pattern (FRONTEND-PATTERNS §Forms).

import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '../../lib/apiError.js';
import { isBusy, isTransportFailure, refusedResponseOf } from '../../lib/refusal.js';
import { Modal } from './Modal.js';
import { Button } from './Button.js';
import { Alert } from './Alert.js';
import { Input } from './Input.js';
import { Label } from './Label.js';

export interface ConfirmDialogProps {
  open: boolean;
  /** The cancel path — Cancel button, Escape, backdrop, X. Locked while pending. */
  onClose: () => void;
  /** Question form: "Delete this user?" */
  title: string;
  /** One-sentence consequence (plain text). For structured content use children. */
  description?: string;
  /** Structured consequence content (cascade list, an Alert, Tags). */
  children?: ReactNode;
  /** The explicit verb on the confirm button ("Delete user") — i18n'd by the caller. */
  confirmLabel: string;
  /** `danger` (default) for destructive; `default` for non-destructive confirms. */
  tone?: 'danger' | 'default';
  /** May return a promise — pending/locking/error handling is built in. */
  onConfirm: () => void | Promise<void>;
  /** Escalation: confirm stays disabled until this exact text is typed. */
  typeToConfirm?: string;
}

export function ConfirmDialog({
  open,
  onClose,
  title,
  description,
  children,
  confirmLabel,
  tone = 'danger',
  onConfirm,
  typeToConfirm,
}: ConfirmDialogProps) {
  const { t } = useTranslation('common');
  const { t: tErrors } = useTranslation('errors');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typed, setTyped] = useState('');
  const typeInputId = useId();

  // Every cancel path (Cancel button, Escape, backdrop, X) funnels through
  // here — resetting state on the way out gives a fresh dialog per open cycle
  // without an effect.
  const close = () => {
    setPending(false);
    setError(null);
    setTyped('');
    onClose();
  };

  const confirmBlocked = typeToConfirm !== undefined && typed !== typeToConfirm;

  const handleConfirm = async () => {
    setError(null);
    try {
      const result = onConfirm();
      if (result instanceof Promise) {
        setPending(true);
        await result;
      }
      close();
    } catch (err) {
      setPending(false);
      if (isBusy(err)) return;
      setError(refusedResponseOf(err) || isTransportFailure(err) ? apiErrorMessage(err, tErrors) : err instanceof Error && err.message ? err.message : t('confirm.failed'));
    }
  };

  return (
    <Modal
      open={open}
      onClose={pending ? () => {} : close}
      title={title}
      description={description}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={close}
            disabled={pending}
            // eslint-disable-next-line jsx-a11y/no-autofocus -- in-dialog safe-default focus (the dialog pattern), not page-load autofocus
            autoFocus={typeToConfirm === undefined}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            onClick={handleConfirm}
            loading={pending}
            disabled={confirmBlocked}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}
      {children}
      {typeToConfirm !== undefined && (
        <div className="mt-3">
          <Label htmlFor={typeInputId}>
            {t('confirm.typePrompt', { value: typeToConfirm })}
          </Label>
          <Input
            id={typeInputId}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            disabled={pending}
            // eslint-disable-next-line jsx-a11y/no-autofocus -- in-dialog safe-default focus: the user must type here before confirm enables
            autoFocus
          />
        </div>
      )}
    </Modal>
  );
}
